import {objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import ErrorSubclass from 'util/ErrorSubclass';

function bartenderUrl(baseUrl: string, subPath: string): string {
  return `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}${subPath}`;
}

export class BartenderError extends ErrorSubclass {}

export interface BartenderServer {
  url: StrictString;
  name: StrictString | null;
}

export interface BartenderPrintersResponse {
  serverPrinters: Array<string>;
  // remotePrinters: Array<string>; // what is this?
}

export interface BartenderLabelTemplate {
  libraryId: string;
  relativePath: string;
}

export interface BartenderServerItem<Item> {
  bartenderServer: BartenderServer;
  item: Item;
}

export type BartenderServerLabelTemplate = BartenderServerItem<BartenderLabelTemplate>;
export type BartenderServerPrinter = BartenderServerItem<string>;

export interface BartenderLibrary {
  id: string;
  name: string;
  // ... some other fields we don't use: description, order, fileFilter, relativePath, validVisibleUsers
}

export interface BartenderLibraryResponse {
  library: BartenderLibrary;
  contents: Array<string>; // list of label template filenames like "container-label-template-small.btw"
}

export interface BartenderDataEntryRequiredResponse {
  success: false;
  printRequestID: string;
  error: string;
  statusCode: 'DataEntryRequired';
  printMessages: Array<string> | null;
  requiredDataEntryControls: {
    [labelTemplateVariable: string]: string;
  };
}

export interface BartenderPrintSuccessResponse {
  success: true;
  printRequestID: string;
  messages: Array<string>;
  filePath: string | null;
}

export interface BartenderPrintErrorResponse {
  success: false;
  printRequestID: string;
  error: string;
  statusCode: string;
  printMessages: Array<string> | null;
  // ... some other fields we don't use
}

export type BartenderPrintResponse =
  | BartenderPrintSuccessResponse
  | BartenderPrintErrorResponse
  | BartenderDataEntryRequiredResponse;

function isBartenderDataEntryRequiredResponse(
  r: BartenderPrintResponse
): r is BartenderDataEntryRequiredResponse {
  return r.success === false && r.statusCode === 'DataEntryRequired';
}

function isBartenderPrintSuccessResponse(r: BartenderPrintResponse): r is BartenderPrintSuccessResponse {
  return r.success === true;
}

export async function fetchBartenderPrinters(
  baseUrl: string,
  abortController?: AbortController
): Promise<Array<string>> {
  // http://help.seagullscientific.com/2021/en/#../Subsystems/PrintPortal/Content/API_Tutorial_EnumeratePrinters.htm?TocPath=Companion%2520Applications%257CPrint%2520Portal%257CAutomating%2520Print%2520Portal%257CUsing%2520the%2520Print%2520Portal%2520REST%2520API%257CAutomation%2520Tutorial%257C_____6
  const response = await fetch(bartenderUrl(baseUrl, 'api/v1/printers'), {signal: abortController?.signal});
  if (!response.ok) {
    throw new BartenderError(`${response.status} ${response.statusText}`);
  }
  const responseJson: BartenderPrintersResponse = await response.json();
  if (responseJson.serverPrinters == null) {
    throw new BartenderError('Unexpected response from server');
  }
  return responseJson.serverPrinters;
}

export async function fetchBartenderLabelTemplates(
  baseUrl: string,
  abortController?: AbortController
): Promise<Array<BartenderLabelTemplate>> {
  // http://help.seagullscientific.com/2021/en/#../Subsystems/PrintPortal/Content/API_Tutorial_EnumerateLibraries.htm?TocPath=Companion%2520Applications%257CPrint%2520Portal%257CAutomating%2520Print%2520Portal%257CUsing%2520the%2520Print%2520Portal%2520REST%2520API%257CAutomation%2520Tutorial%257C_____4
  const librariesUrl = bartenderUrl(baseUrl, 'api/v1/libraries');
  const librariesResponse = await fetch(librariesUrl, {signal: abortController?.signal});
  if (!librariesResponse.ok) {
    throw new BartenderError(`${librariesResponse.status} ${librariesResponse.statusText}`);
  }
  const librariesResponseJson: Array<BartenderLibrary> = await librariesResponse.json();
  if (!Array.isArray(librariesResponseJson)) {
    throw new BartenderError(`Unexpected response from server`);
  }
  const librarianLibrary = librariesResponseJson.find((library) => library.name === 'Librarian');
  if (librarianLibrary == null) {
    throw new BartenderError(`Bartender library "Librarian" not found`);
  }

  // Fetch "Librarian" library which has list of templates. "Librarian" is a special BarTender library which
  // is automatically available via the REST API and supports revision control of the label templates. To
  // keep things simple and enforce best practices, Benchling requires label templates to be in Librarian.
  // http://help.seagullscientific.com/2021/en/#../Subsystems/PrintPortal/Content/API_Tutorial_EnumerateLibraryItems.htm?TocPath=Companion%2520Applications%257CPrint%2520Portal%257CAutomating%2520Print%2520Portal%257CUsing%2520the%2520Print%2520Portal%2520REST%2520API%257CAutomation%2520Tutorial%257C_____5
  const libraryUrl = bartenderUrl(baseUrl, `api/v1/libraries/${librarianLibrary.id}`);
  const libraryResponse = await fetch(libraryUrl, {signal: abortController?.signal});
  if (!libraryResponse.ok) {
    throw new BartenderError(`${libraryResponse.status} ${libraryResponse.statusText}`);
  }

  const libraryResponseJson: BartenderLibraryResponse = await libraryResponse.json();
  if (libraryResponseJson.library == null || libraryResponseJson.contents == null) {
    throw new BartenderError(`Unexpected response from server`);
  }
  return libraryResponseJson.contents.map((relativePath) => ({
    libraryId: librarianLibrary.id,
    relativePath,
  }));
}

function postJson(url: string, body: unknown, abortController?: AbortController): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: {'content-type': 'application/json'},
    body: JSON.stringify(body),
    signal: abortController?.signal,
  });
}

/** Make initial print request without label params */
async function makeInitialPrintRequest({
  bartenderBaseUrl,
  labelTemplate,
  printer,
  copies,
  abortController,
}: {
  bartenderBaseUrl: string;
  labelTemplate: BartenderLabelTemplate;
  printer: string;
  copies: number;
  abortController?: AbortController;
}): Promise<Response> {
  return await postJson(
    bartenderUrl(bartenderBaseUrl, 'api/v1/print'),
    {
      libraryID: labelTemplate.libraryId,
      relativePath: labelTemplate.relativePath,
      printer,
      copies,
    },
    abortController
  );
}

/**
 * Make followup print request with label params. `labelParams` keys are expected to be lowercase. They come
 * back this way from `labelParamsJson` graphql.
 */
async function makeFollowupPrintRequest({
  bartenderBaseUrl,
  abortController,
  labelTemplate,
  labelParams,
  printer,
  copies,
  dataEntryRequiredResponse,
}: {
  bartenderBaseUrl: string;
  abortController?: AbortController;
  labelTemplate: BartenderLabelTemplate;
  labelParams: {[param: string]: string};
  printer: string;
  copies: number;
  dataEntryRequiredResponse: BartenderDataEntryRequiredResponse;
}): Promise<BartenderPrintSuccessResponse> {
  const url = bartenderUrl(bartenderBaseUrl, 'api/v1/print');
  const jsonPayload = {
    libraryID: labelTemplate.libraryId,
    relativePath: labelTemplate.relativePath,
    printer,
    copies,
    printRequestID: dataEntryRequiredResponse.printRequestID,
    dataEntryControls: objectFromEntries(
      Object.entries(dataEntryRequiredResponse.requiredDataEntryControls).map(([key, _]) => [
        key,
        labelParams[key.toLowerCase()] ?? '',
      ])
    ),
  };
  const response = await postJson(url, jsonPayload, abortController);

  let responseJson: BartenderPrintResponse;
  try {
    responseJson = await response.json();
  } catch (e) {
    throw new BartenderError(`[2] ${response.status} ${response.statusText} (could not parse response body)`);
  }

  if (responseJson.success === true) {
    return responseJson;
  } else if (responseJson.success === false) {
    throw new BartenderError(
      `[2] ${responseJson.statusCode} ${responseJson.error}: ${responseJson.printMessages?.join('\n') ?? ''}`
    );
  } else {
    throw new BartenderError(
      `[2] ${response.status} ${response.statusText}: unexpected response from server`
    );
  }
}

async function loadResponseJson(
  response: Response
): Promise<BartenderPrintSuccessResponse | BartenderDataEntryRequiredResponse> {
  let responseJson: BartenderPrintResponse;
  try {
    responseJson = await response.json();
  } catch (e) {
    throw new BartenderError(`[1] ${response.status} ${response.statusText} (could not parse response body)`);
  }

  if (!isBartenderPrintSuccessResponse(responseJson) && !isBartenderDataEntryRequiredResponse(responseJson)) {
    if (responseJson.success === false) {
      throw new BartenderError(
        `[1] ${responseJson.statusCode} ${responseJson.error}: ${
          responseJson.printMessages?.join('\n') ?? ''
        }`
      );
    } else {
      throw new BartenderError(
        `[1] ${response.status} ${response.statusText}: unexpected response from server`
      );
    }
  }

  return responseJson;
}

/**
 * Print one or more copies of a label using BarTender.
 *
 * This function is only called by `bartenderRequestPreview` and is exported only for tests.
 *
 * See `bartenderPrintItems` for general information about how labels are printed using the BarTender API.
 *
 * `labelParams` keys are expected to be lowercase. They come back this way from `labelParamsJson` graphql.
 */
export async function bartenderPrintItem({
  bartenderBaseUrl,
  abortController,
  labelTemplate,
  labelParams,
  printer,
  copies,
}: {
  bartenderBaseUrl: string;
  abortController?: AbortController;
  labelTemplate: BartenderLabelTemplate;
  labelParams: {[param: string]: string};
  printer: string;
  copies: number;
}): Promise<BartenderPrintSuccessResponse> {
  const response = await makeInitialPrintRequest({
    bartenderBaseUrl,
    labelTemplate,
    printer,
    copies,
    abortController,
  });

  const responseJson = await loadResponseJson(response);

  if (isBartenderDataEntryRequiredResponse(responseJson)) {
    // The common case where we send a second request with parameter values
    return await makeFollowupPrintRequest({
      bartenderBaseUrl,
      abortController,
      labelTemplate,
      labelParams,
      printer,
      copies,
      dataEntryRequiredResponse: responseJson,
    });
  } else {
    // The label template has no parameters
    assert(
      isBartenderPrintSuccessResponse(responseJson),
      'Response is not a bartender print success response'
    );
    return responseJson;
  }
}

/**
 * Print one or more copies of labels for one or more items using BarTender.
 *
 * Usually labels printed from Benchling will have parameterized elements whose values need to be populated.
 * As explained in the documentation:
 *
 * > Files that contain form data require two API passes. You will need to submit the first request in order
 * > to get an error message that will list a required "printRequestID.”
 *
 * http://help.seagullscientific.com/2021/en/#../Subsystems/PrintPortal/Content/API_Tutorial_PrintSimpleDoc.htm?TocPath=Companion%2520Applications%257CPrint%2520Portal%257CAutomating%2520Print%2520Portal%257CUsing%2520the%2520Print%2520Portal%2520REST%2520API%257CAutomation%2520Tutorial%257C_____7
 *
 * In other words the response to the initial request will be a `BartenderDataEntryRequiredResponse`, which
 * has `statusCode="DataEntryRequired"` and a mapping called `requiredDataEntryControls` which lists the
 * parameters that need to be supplied. The function `makeFollowupPrintRequest` (above) sends the second
 * request, supplying the `printRequestId` and `dataEntryControls`. There is no way around making two
 * requests.
 *
 * The BarTender API doesn't support bulk operations, so this function issues one initial request and
 * usually one followup request for each label. For improved printing speed, we send the first set of
 * requests all at once. But to guarantee that labels are printed in the expected order, which is critical,
 * we send the followup print requests serially.
 *
 * If there is an error printing any of them, any remaining items are not printed. Thus partial success is
 * a possibility, where say the first 3 labels are printed and the last 5 are not.
 *
 * The implementation is a little tricky. To send the followup print requests serially, we build a promise
 * chain where the next request is sent only when the previous returns successfully.
 *
 * Since partial success is possible, the return value is a promise resolving to `{successCount, error}`.
 *
 * `printableItemsLabelParams` keys are expected to be lowercase. They come back this way from
 * `labelParamsJson` graphql.
 */
export function bartenderPrintItems({
  bartenderBaseUrl,
  labelTemplate,
  printableItemsLabelParams,
  printer,
  copies,
}: {
  bartenderBaseUrl: string;
  printableItemsLabelParams: Array<{[param: string]: string}>;
  labelTemplate: BartenderLabelTemplate;
  printer: string;
  copies: number;
}): Promise<{numSuccessfulPrints: number; error: unknown | null}> {
  let successCount = 0;
  let i = 0;

  let followupRequestChain: Promise<void | BartenderPrintSuccessResponse> = Promise.resolve();
  const promises = [...Array(printableItemsLabelParams.length)].map(() =>
    makeInitialPrintRequest({bartenderBaseUrl, labelTemplate, printer, copies})
      .then((response) => loadResponseJson(response))
      .then((responseJson) => {
        if (isBartenderDataEntryRequiredResponse(responseJson)) {
          // The common case where we send a second request with parameter values. We use `i` here to
          // make sure the labels are printed in the correct order, since the responses to the initial set
          // of requests come back in no particular order.
          const labelParams = printableItemsLabelParams[i];
          i++;
          followupRequestChain = followupRequestChain
            .then(() =>
              makeFollowupPrintRequest({
                bartenderBaseUrl,
                labelTemplate,
                labelParams,
                printer,
                copies,
                dataEntryRequiredResponse: responseJson,
              })
            )
            .then((response) => {
              successCount++;
            });
        } else if (isBartenderPrintSuccessResponse(responseJson)) {
          // If the initial response has success=true, the label template has no parameters
          successCount++;
        }
      })
  );

  let error: unknown | null = null;
  return Promise.allSettled(promises)
    .then((results: Array<{status: 'fulfilled'} | {status: 'rejected'; reason: unknown}>) => {
      for (const result of results) {
        if (result.status === 'rejected') {
          // note error from one of the initial requests
          error = result.reason;
          break;
        }
      }
      // return chain of followup requests
      return followupRequestChain;
    })
    .catch((reason: unknown) => {
      // note error from one of the followup requests
      error = reason;
    })
    .then(() => ({
      numSuccessfulPrints: successCount,
      error,
    }));
}

// `labelParams` keys are expected to be lowercase. They come back this way from `labelParamsJson` graphql.
export async function bartenderRequestPreview({
  bartenderBaseUrl,
  abortController,
  labelTemplate,
  labelParams,
}: {
  bartenderBaseUrl: string;
  abortController?: AbortController;
  labelTemplate: BartenderLabelTemplate;
  labelParams: {[param: string]: string};
}): Promise<string> {
  const printResponse = await bartenderPrintItem({
    bartenderBaseUrl,
    abortController,
    labelTemplate,
    labelParams,
    printer: 'PDF',
    copies: 1,
  });
  if (printResponse.filePath == null) {
    throw new BartenderError(
      'Problem requesting print preview: response from BarTender server does not contain "filePath".'
    );
  }
  return printResponse.filePath;
}

/**
 * Takes a BartenderServer that may have extraneous fields (typically GQL stuff like __typename, id) and
 * removes them. Needed because the backend API endpoints won't tolerate the extra junk.
 */
export function makePlainBartenderServer({url, name}: BartenderServer): BartenderServer {
  return {url, name};
}
